import React, { FC, ReactElement } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import get from 'lodash/get';
import set from 'lodash/set';
// Components
import Text from '../text';
import InputField, { InputFieldProps } from '../form/input';
import ModalButton, { ButtonType } from './templates/modalButton';
import ModalButtonContainer from './templates/modalButtonContainer';
// Resources
import { stringsRes } from '../../resources/strings';
import { PropsFromModal } from './createModalHOC';
import useGlobalStyles from '../hook/useGlobalStyles';

interface CrudFormProps extends PropsFromModal {
    mode?: 'add' | 'edit';
    formData?: any;
    description?: string;
    customFields?: InputFieldProps[];
    labelDelete?: string;
    typeDelete?: ButtonType;
    labelCancel?: string;
    typeCancel?: ButtonType;
    labelConfirm?: string;
    typeConfirm?: ButtonType;
    nameInputProps?: InputFieldProps;
    descriptionInputProps?: InputFieldProps;
    /** Returns a field name -> error message map. Any entry blocks the confirm button. */
    validate?: (data: any) => Record<string, string>;
    onConfirmAction?: (data: any) => void;
    onCancelAction?: () => void;
    onDeleteAction?: () => void;
}

const CrudForm: FC<CrudFormProps> = (props): ReactElement => {
    const {
        onRequestClose,
        mode = 'add',
        formData = {},
        description,
        customFields,
        labelDelete,
        typeDelete,
        labelCancel,
        typeCancel,
        labelConfirm,
        typeConfirm,
        nameInputProps,
        descriptionInputProps,
        validate,
        onConfirmAction,
        onCancelAction,
        onDeleteAction,
    } = props;
    const classes = useStyle();
    const globalClasses = useGlobalStyles();

    const [data, setData] = React.useState<any>(formData);

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // console.log(data, e.target.name, e.target.value);
        const newState = set({ ...data }, e.target.name, e.target.value);
        setData(newState);
    };

    const errors = validate?.(data) ?? {};
    const hasError = Object.keys(errors).length > 0;

    const onClickConfirm = (): void => {
        if (hasError) {
            return;
        }
        onConfirmAction?.(data);
        onRequestClose();
    };

    const onClickCancel = (): void => {
        onCancelAction?.();
        onRequestClose();
    };

    const onClickDelete = (): void => {
        onDeleteAction?.();
        onRequestClose();
    };

    const checkRequired = (): boolean => {
        return customFields?.some(
            (cf) =>
                (cf.required && !(get(data, `${cf.name}`) || '').trim()) ||
                (cf.test?.regex && !cf.test.regex.test(get(data, `${cf.name}`) as string)),
        );
    };

    const isEdit = mode === 'edit';

    return (
        <div className="LCCrudFormContainer">
            {description && (
                <div className={globalClasses.flexCol}>
                    <Text.Description16 customColor="grey" text={description} />
                </div>
            )}
            {customFields ? (
                <div className={classes.formContent}>
                    {customFields.map((cf) => (
                        <InputField
                            key={cf.name}
                            {...cf}
                            onChange={onChange}
                            className={classes.filterInput}
                            value={get(data, `${cf.name}`) || ''}
                            errorText={errors[cf.name] ?? cf.errorText}
                        />
                    ))}
                </div>
            ) : (
                <div className={classes.formContent}>
                    <InputField
                        name="name"
                        required
                        onChange={onChange}
                        className={classes.filterInput}
                        value={get(data, `name`) || ''}
                        label={stringsRes.policies.costCenter.modal.form.name}
                        {...nameInputProps}
                        errorText={errors.name ?? nameInputProps?.errorText}
                    />
                    <InputField
                        name="description"
                        onChange={onChange}
                        className={classes.filterInput}
                        value={get(data, `description`) || ''}
                        label={stringsRes.policies.costCenter.modal.form.description}
                        {...descriptionInputProps}
                    />
                </div>
            )}
            <ModalButtonContainer>
                {isEdit && onDeleteAction && (
                    <ModalButton
                        type={typeDelete ?? ButtonType.ERROR}
                        onClick={onClickDelete}
                        label={labelDelete ?? stringsRes.common.delete}
                    />
                )}
                <div className={classes.flex} />
                <ModalButton
                    type={typeCancel ?? ButtonType.CANCEL}
                    onClick={onClickCancel}
                    label={labelCancel ?? stringsRes.common.cancel}
                />
                <ModalButton
                    type={typeConfirm ?? ButtonType.CONFIRM}
                    onClick={onClickConfirm}
                    disabled={hasError || (customFields ? checkRequired() : !data?.name?.trim())}
                    label={labelConfirm ?? (isEdit ? stringsRes.common.edit : stringsRes.common.confirm)}
                />
            </ModalButtonContainer>
        </div>
    );
};

const useStyle = makeStyles(() =>
    createStyles({
        formContent: {
            display: 'flex',
            flexDirection: 'column',
            marginTop: 16,
        },
        filterInput: {},
        flex: {
            display: 'flex',
            flex: 1,
        },
    }),
);

export default CrudForm;
